import { useCallback, useEffect, useRef, useState } from 'react';

import { ClaimDetailsModal } from '@/components/ClaimDetailsModal';
import { ClaimSubmissionModal } from '@/components/ClaimSubmissionModal';
import { ClaimsList } from '@/components/ClaimsList';
import { ErrorDisplay } from '@/components/ErrorDisplay';
import { Header } from '@/components/Header';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { PageContainer } from '@/components/PageContainer';
import { Pagination } from '@/components/Pagination';

import { useAuth } from '@/hooks/useAuth';
import { useClaims } from '@/hooks/useClaims';
import { useCursorPagination } from '@/hooks/useCursorPagination';

import type { IClaim } from '@/types/claim';

function ClaimsHistory() {
  const { user, signOut } = useAuth();
  const [isMobile, setIsMobile] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState<IClaim | null>(null);
  const [editingClaim, setEditingClaim] = useState<IClaim | null>(null);
  const [accumulatedClaims, setAccumulatedClaims] = useState<IClaim[]>([]);
  const hasAnimatedRef = useRef(false);
  const prevMobileRef = useRef(false);

  const ITEMS_PER_PAGE_DESKTOP = 20;
  const ITEMS_PER_PAGE_MOBILE_INITIAL = 10;
  const ITEMS_PER_PAGE_MOBILE_LOAD_MORE = 5;

  const {
    currentPage,
    exclusiveStartKey,
    resetPagination,
    handlePageChange: handlePageChangeInternal,
  } = useCursorPagination<string | null>();

  const getMobileLimit = () => {
    if (!isMobile) return ITEMS_PER_PAGE_DESKTOP;
    return exclusiveStartKey === null && currentPage === 1
      ? ITEMS_PER_PAGE_MOBILE_INITIAL
      : ITEMS_PER_PAGE_MOBILE_LOAD_MORE;
  };

  const {
    data: claims,
    hasMore,
    nextCursor: responseKey,
    loading,
    error,
    refetch,
  } = useClaims({
    limit: getMobileLimit(),
    exclusiveStartKey: exclusiveStartKey,
  });

  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect
    setIsVisible(true);
    const timer = setTimeout(() => {
      hasAnimatedRef.current = true;
    }, 1100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (isMobile && claims.length > 0) {
      if (currentPage === 1) {
        // oxlint-disable-next-line react/set-state-in-effect
        setAccumulatedClaims(claims);
      } else {
        setAccumulatedClaims((prev) => {
          const existingIds = new Set(prev.map((c) => c.id));
          const newClaims = claims.filter((c) => !existingIds.has(c.id));
          return [...prev, ...newClaims];
        });
      }
    }
  }, [claims, isMobile, currentPage]);

  useEffect(() => {
    const checkMobile = () => {
      const nowMobile = window.innerWidth < 768;
      if (prevMobileRef.current !== nowMobile) {
        setAccumulatedClaims([]);
        resetPagination();
      }
      prevMobileRef.current = nowMobile;
      setIsMobile(nowMobile);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [resetPagination]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      handlePageChangeInternal(newPage, {
        hasMore,
        responseKey: responseKey ?? null,
      });
    },
    [handlePageChangeInternal, hasMore, responseKey],
  );

  useEffect(() => {
    if (!isMobile || !hasMore || loading) return;

    const handleScroll = () => {
      if (
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 100 &&
        hasMore &&
        responseKey &&
        !loading
      ) {
        handlePageChange(currentPage + 1);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile, hasMore, responseKey, currentPage, handlePageChange, loading]);

  if (error) {
    return <ErrorDisplay error={error} onRetry={refetch} onLogout={signOut} />;
  }

  if (!user) {
    return null;
  }

  // oxlint-disable-next-line react/refs
  const hasAnimated = hasAnimatedRef.current;

  return (
    <div className="min-h-screen bg-white">
      <Header user={user} onSignOut={signOut} />
      <PageContainer>
        <div className="mx-auto max-w-4xl">
          <header className="mb-8">
            <h1
              className={`mb-8 text-center text-3xl font-bold text-gray-900 transition-all duration-700 ${
                isVisible ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'
              }`}
            >
              My Claim History
            </h1>
          </header>

          {loading && (isMobile ? accumulatedClaims.length === 0 : claims.length === 0) ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner message="Loading claims history..." />
            </div>
          ) : (isMobile ? accumulatedClaims.length === 0 : claims.length === 0) ? (
            <div
              data-testid="claims-history"
              className={`rounded-lg border border-gray-200 bg-white p-12 text-center ${
                hasAnimated
                  ? ''
                  : `transition-opacity transition-transform delay-300 duration-700 ${
                      isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
                    }`
              }`}
            >
              <div className="mx-auto max-w-md">
                <div className="mx-auto mb-4 flex size-20 items-center justify-center rounded-full bg-gray-100">
                  <svg
                    className="size-10 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <h3
                  className="mb-2 text-lg font-semibold text-gray-900"
                  data-testid="claims-empty-state"
                >
                  No claims yet
                </h3>
                <p className="text-gray-600">
                  Submit your first claim to get started! Your claim history will appear here.
                </p>
              </div>
            </div>
          ) : (
            <div
              data-testid="claims-history"
              className={`flex flex-col ${
                hasAnimated
                  ? ''
                  : `transition-all delay-300 duration-700 ${
                      isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
                    }`
              }`}
              style={
                !isMobile
                  ? { height: 'calc(100vh - 3.5rem - 2rem - 2rem - 5rem - 4rem)' }
                  : undefined
              }
            >
              <ClaimsList
                claims={isMobile ? accumulatedClaims : claims}
                isMobile={isMobile}
                hasAnimated={hasAnimated}
                isVisible={isVisible}
                showIcon={false}
                onClaimClick={setSelectedClaim}
              />

              {!isMobile && (currentPage > 1 || hasMore) && (
                <div
                  className={`mt-8 shrink-0 ${
                    hasAnimated
                      ? ''
                      : `transition-all duration-700 ${
                          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
                        }`
                  }`}
                  style={hasAnimated ? undefined : { transitionDelay: '400ms' }}
                >
                  <Pagination
                    currentPage={currentPage}
                    hasMore={hasMore}
                    hasPrevious={currentPage > 1}
                    pageSize={ITEMS_PER_PAGE_DESKTOP}
                    currentItemsCount={claims.length}
                    totalPages={currentPage + (hasMore ? 1 : 0)}
                    onPageChange={handlePageChange}
                  />
                </div>
              )}

              {isMobile && loading && (
                <div className="mt-4 text-center text-sm text-gray-500">Loading more claims...</div>
              )}
            </div>
          )}
        </div>
      </PageContainer>
      {editingClaim && (
        <ClaimSubmissionModal claim={editingClaim} onClose={() => setEditingClaim(null)} />
      )}
      {selectedClaim && (
        <ClaimDetailsModal
          claim={selectedClaim}
          onClose={() => setSelectedClaim(null)}
          onEditClick={(claim) => {
            setSelectedClaim(null);
            setEditingClaim(claim);
          }}
        />
      )}
    </div>
  );
}

export { ClaimsHistory };
